import { Box, SxProps, SvgIcon, Theme, Typography, useTheme } from '@mui/material';
import { ImageNotSupported } from '@mui/icons-material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface RecipeImageTileProps {
  imageUrl?: string;
  alt: string;
  /** Tile height - card-sized tiles use a smaller height than the large detail-page hero. */
  height?: number | string;
  sx?: SxProps<Theme>;
  onClick?: () => void;
}

/**
 * Recipe hero image tile - reuses the same visual treatment as the Product image tile
 * (`Procurement/Products/ProductForm.tsx`): a bordered/rounded frame with the photo covering
 * the tile, falling back to an `ImageNotSupported` icon + "No image available" text when
 * there's no image or it fails to load. Shared across RecipeBank's card grid, RecipeBank's
 * detail page and the Public Menu recipe view so the "no photo yet" state looks identical
 * everywhere.
 */
const RecipeImageTile = ({ imageUrl, alt, height = 160, sx, onClick }: RecipeImageTileProps) => {
  const { t } = useTranslation('recipeBank');
  const theme = useTheme();
  const [loadFailed, setLoadFailed] = useState(false);

  return (
    <Box
      onClick={onClick}
      sx={{
        position: 'relative',
        width: '100%',
        height,
        borderRadius: '12px',
        overflow: 'hidden',
        border: '1px solid',
        borderColor: theme.palette.custom.gray[300],
        backgroundColor: theme.palette.custom.gray[50],
        cursor: onClick ? 'pointer' : 'default',
        ...sx,
      }}
    >
      {imageUrl && !loadFailed ? (
        <Box
          component="img"
          src={imageUrl}
          alt={alt}
          onError={() => setLoadFailed(true)}
          sx={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }}
        />
      ) : (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <SvgIcon sx={{ color: theme.palette.custom.gray[500] }}>
            <ImageNotSupported />
          </SvgIcon>
          <Typography variant="textXS"
            color="text.secondary">
            {t('noImageAvailable', 'No image available')}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default RecipeImageTile;
